import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState, useMemo, useCallback } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Plus, Edit2, Trash2, Search, RefreshCcw, Users,
} from 'lucide-react';
import {
  createUrithiMonthlyService, deleteUthiriMonthlyServices,
  fetchUrithiMonthlyServices, fetchUrithiSubscriptions,
  updateUrithiMonthlyServices,
} from './hooks/hooks';
import {
  Toast, LoadingState, ErrorState, EmptyState, Pagination,
  BackButton, ConfirmModal, StatusBadge,
} from './ui';

const schema = Yup.object({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
  price_per_month: Yup.number().positive().min(0.01).required('Required'),
});

const inputCls = 'w-full px-3.5 py-2.5 border border-gray-200 text-sm rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 transition-all bg-white placeholder:text-gray-400';

const Field = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-bold text-[#001f3f] uppercase tracking-wide block">{label}</label>
    {children}
    {error && <p className="text-[11px] text-red-500 font-medium">{error}</p>}
  </div>
);

function UrithiMonthlyServices() {
  const queryClient = useQueryClient();

  const { data: services, isLoading, isError, refetch } = useQuery({
    queryKey: ['monthlyServices'],
    queryFn: fetchUrithiMonthlyServices,
  });
  const { data: subscriptions } = useQuery({
    queryKey: ['monthlyServicesSubscriptions'],
    queryFn: fetchUrithiSubscriptions,
  });

  const [tab, setTab] = useState(0);
  const [open, setOpen] = useState({ create: false, update: false });
  const [selectedItem, setSelectedItem] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });
  const [searchServices, setSearchServices] = useState('');
  const [searchSubs, setSearchSubs] = useState('');
  const [servicesPage, setServicesPage] = useState(0);
  const [subsPage, setSubsPage] = useState(0);
  const [servicesRows, setServicesRows] = useState(5);
  const [subsRows, setSubsRows] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });

  const filteredServices = useMemo(() =>
    Array.isArray(services)
      ? services.filter(s => s.name?.toLowerCase().includes(searchServices.toLowerCase()))
      : []
  , [services, searchServices]);

  const filteredSubs = useMemo(() =>
    Array.isArray(subscriptions)
      ? subscriptions.filter(s =>
          s.user?.id?.toString().toLowerCase().includes(searchSubs.toLowerCase()) ||
          s.user?.email?.toLowerCase().includes(searchSubs.toLowerCase()) ||
          s.service_plan?.id?.toString().toLowerCase().includes(searchSubs.toLowerCase()) ||
          s.service_plan?.name?.toLowerCase().includes(searchSubs.toLowerCase())
        )
      : []
  , [subscriptions, searchSubs]);

  // Mutations
  const mutation = useMutation({
    mutationFn: createUrithiMonthlyService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['monthlyServices'] });
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Service added successfully!', severity: 'success' });
      formik.resetForm();
      refetch();
    },
    onError: () => setToast({ open: true, message: 'Failed to add service.', severity: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: updateUrithiMonthlyServices,
    onSuccess: () => {
      queryClient.invalidateQueries(['monthlyServices']);
      setOpen({ create: false, update: false });
      setToast({ open: true, message: 'Service updated successfully!', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to update service.', severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUthiriMonthlyServices,
    onSuccess: () => {
      queryClient.invalidateQueries(['monthlyServices']);
      setDeleteConfirm({ open: false, id: null });
      setToast({ open: true, message: 'Service deleted successfully!', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to delete service.', severity: 'error' }),
  });

  const formik = useFormik({
    initialValues: { name: '', description: '', price_per_month: '' },
    validationSchema: schema,
    onSubmit: (v) => mutation.mutate(v),
  });

  const upformik = useFormik({
    initialValues: { name: '', description: '', price_per_month: '' },
    validationSchema: schema,
    onSubmit: (v) => {
      if (selectedItem?.id) updateMutation.mutate({ id: selectedItem.id, data: v });
    },
  });

  const handleOpenUpdate = useCallback((item) => {
    setSelectedItem(item);
    upformik.setValues({
      name: item.name || '',
      description: item.description || '',
      price_per_month: item.price_per_month || '',
    });
    setOpen({ create: false, update: true });
  }, [upformik]);

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState onRetry={refetch} />;

  // Form view
  if (open.create || open.update || services?.length === 0) {
    const isUpdate = open.update;
    const fk = isUpdate ? upformik : formik;
    const showBack = open.create || open.update;

    return (
      <div>
        <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
        {showBack && <BackButton onClick={() => setOpen({ create: false, update: false })} label="Back to Services" />}

        <div className="bg-white rounded-2xl border border-gray-100 p-5 sm:p-7">
          <div className="mb-6">
            <h2 className="text-base font-bold text-[#001f3f] uppercase tracking-wide mb-1">
              {isUpdate ? 'Update Monthly Service' : (services?.length === 0 ? 'Create Your First Service' : 'Add New Monthly Service')}
            </h2>
            <p className="text-xs text-gray-500">Define a recurring monthly service plan</p>
          </div>

          <form onSubmit={fk.handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Service Name" error={fk.touched.name && fk.errors.name}>
                <input
                  type="text"
                  name="name"
                  value={fk.values.name}
                  onChange={fk.handleChange}
                  onBlur={fk.handleBlur}
                  className={inputCls}
                  placeholder="e.g. Premium Support"
                />
              </Field>

              <Field label="Price per Month (TZS)" error={fk.touched.price_per_month && fk.errors.price_per_month}>
                <div className="relative">
                  <span className="absolute left-3.5 top-2.5 text-xs text-gray-500 font-bold">TZS</span>
                  <input
                    type="number"
                    name="price_per_month"
                    value={fk.values.price_per_month}
                    onChange={fk.handleChange}
                    onBlur={fk.handleBlur}
                    className={`${inputCls} pl-12`}
                    placeholder="0.00"
                  />
                </div>
              </Field>
            </div>

            <Field label="Description" error={fk.touched.description && fk.errors.description}>
              <textarea
                name="description"
                value={fk.values.description}
                onChange={fk.handleChange}
                onBlur={fk.handleBlur}
                rows={5}
                className={`${inputCls} resize-y`}
                placeholder="Service description"
              />
            </Field>

            <button
              type="submit"
              disabled={mutation.isLoading || updateMutation.isLoading}
              className="w-full bg-[#001f3f] hover:bg-[#0a3260] text-white py-3.5 rounded-xl text-sm font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
            >
              {(mutation.isLoading || updateMutation.isLoading) ? 'Processing…' : (isUpdate ? 'Update Service' : 'Add Service')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // List view with tabs
  const tabs = [
    { label: 'Service Plans', icon: RefreshCcw, count: filteredServices.length },
    { label: 'Subscriptions', icon: Users, count: filteredSubs.length },
  ];

  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete Service"
        message="Are you sure you want to delete this service plan? This action cannot be undone."
        confirmLabel="Yes, Delete"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-gray-100 bg-gray-50/50">
          {tabs.map((t, idx) => {
            const Icon = t.icon;
            const active = tab === idx;
            return (
              <button
                key={t.label}
                onClick={() => setTab(idx)}
                className={`flex items-center gap-2 px-5 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                  active ? 'text-[#001f3f] bg-white border-b-2 border-[#001f3f]' : 'text-gray-500 hover:text-[#001f3f]'
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${active ? 'bg-[#001f3f] text-white' : 'bg-gray-200 text-gray-600'}`}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Service Plans tab */}
        {tab === 0 && (
          <>
            <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-bold text-[#001f3f] uppercase tracking-wide">Service Plans</h3>
                <button
                  onClick={() => setOpen({ create: true, update: false })}
                  className="inline-flex items-center gap-1.5 bg-[#001f3f] hover:bg-[#0a3260] text-white px-3.5 py-2 rounded-full text-xs font-bold transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Service
                </button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search services…"
                  value={searchServices}
                  onChange={(e) => setSearchServices(e.target.value)}
                  className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-64 bg-gray-50"
                />
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    {['Name', 'Description', 'Price/Month', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredServices.slice(servicesPage * servicesRows, servicesPage * servicesRows + servicesRows).map(s => (
                    <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-4 py-3 text-xs font-bold text-[#001f3f]">{s.name}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-md truncate">{s.description}</td>
                      <td className="px-4 py-3 text-xs font-bold text-emerald-600">TZS {Number(s.price_per_month).toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleOpenUpdate(s)} className="p-2 rounded-lg text-[#001f3f] hover:bg-[#001f3f]/10 transition-colors">
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setDeleteConfirm({ open: true, id: s.id })} className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={servicesPage}
              rowsPerPage={servicesRows}
              total={filteredServices.length}
              onPageChange={setServicesPage}
              onRowsPerPageChange={(r) => { setServicesRows(r); setServicesPage(0); }}
            />
          </>
        )}

        {/* Subscriptions tab */}
        {tab === 1 && (
          subscriptions?.length === 0 ? (
            <EmptyState title="No Subscriptions Yet" message="Customer subscriptions will appear here." />
          ) : (
            <>
              <div className="p-5 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                <h3 className="text-sm font-bold text-[#001f3f] uppercase tracking-wide">Subscriptions</h3>
                <div className="relative">
                  <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by client, service, or email…"
                    value={searchSubs}
                    onChange={(e) => setSearchSubs(e.target.value)}
                    className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-72 bg-gray-50"
                  />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Service', 'Client', 'Months', 'Amount', 'Start', 'End', 'Status', 'Method'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filteredSubs.slice(subsPage * subsRows, subsPage * subsRows + subsRows).map(s => (
                      <tr key={s.id} className="hover:bg-gray-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-xs font-bold text-[#001f3f]">#{s.service_plan?.id} {s.service_plan?.name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-xs font-medium text-gray-700">{s.user?.name}</p>
                          <p className="text-[11px] text-gray-500">{s.user?.email}</p>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-700">{s.quantity}</td>
                        <td className="px-4 py-3 text-xs font-bold text-emerald-600">TZS {Number(s.total_price).toLocaleString()}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{s.start_date}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{s.end_date}</td>
                        <td className="px-4 py-3"><StatusBadge status={s.payment_status} /></td>
                        <td className="px-4 py-3 text-xs text-gray-700 capitalize">{s.payment_method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={subsPage}
                rowsPerPage={subsRows}
                total={filteredSubs.length}
                onPageChange={setSubsPage}
                onRowsPerPageChange={(r) => { setSubsRows(r); setSubsPage(0); }}
              />
            </>
          )
        )}
      </div>
    </div>
  );
}

export default UrithiMonthlyServices;
// import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// import React, { useState } from 'react'
// import { createUrithiMonthlyService, deleteUthiriMonthlyServices, fetchUrithiMonthlyServices, fetchUrithiSubscriptions, updateUrithiMonthlyServices } from './hooks/hooks';
// import { Alert, Box, Button, Container, Grid, IconButton, InputAdornment, Paper, Snackbar, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tabs, TextField, Typography } from '@mui/material';
// import { useFormik } from 'formik';
// import * as Yup from 'yup';
// import { Add, ArrowBack, Delete, Edit } from '@mui/icons-material';

// // Validation Schema for Form Fields using Yup
// const validationSchema = Yup.object({
//     name: Yup.string().required('Service name is required'),
//     description: Yup.string().required('Service description is required'),
//     price_per_month: Yup.number()
//       .required('Price is required')
//       .positive('Price must be a positive number')
//       .min(0.01, 'Price must be greater than zero'),
//   });

//   const upValidationSchema = Yup.object({
//     name: Yup.string().required('Service name is required'),
//     description: Yup.string().required('Service description is required'),
//     price_per_month: Yup.number()
//       .required('Price is required')
//       .positive('Price must be a positive number')
//       .min(0.01, 'Price must be greater than zero'),
//   });

// const UrithiMonthlyServices = () => {
//   const queryClient = useQueryClient(); // React Query fetch hook
//   const { data: assets, isLoading, isError, refetch } = useQuery({
//      queryKey: ["monthlyservices"],
//      queryFn: fetchUrithiMonthlyServices,
//  });  
//  const { data: subscriptions, isLoading:subscriptionsIsLoading, isError:subscriptionsIsError, refetch:subscriptionsRefetch } = useQuery({
//     queryKey: ["monthlyservicessubscriptions"],
//     queryFn: fetchUrithiSubscriptions,
// }); 

 
//     const [tabValue, setTabValue] = useState(0);

//     const handleTabChange = (event, newValue) => {
//         setTabValue(newValue);
//     };
    
//   const [selectedItem, setSelectedItem] = useState(null);
//  const [open, setOpen] = useState({ create: false, update: false, detail: false });
//  const [toast, setToast] = useState({ open: false, message: "", severity: "" });
//  const [searchQuery, setSearchQuery] = useState("");
//  const [searchSubscriptionsQuery, setSearchSubscriptionsQuery] = useState("");
//  const [sortBy, setSortBy] = useState("date"); // Default sorting by date
//  const [page, setPage] = useState(0);
//  const [rowsPerPage, setRowsPerPage] = useState(5);
//  const [subscriptionPage, setSubscriptionPage] = useState(0);
//  const [rowsPerSubscriptionPage, setRowsPerSubscriptionPage] = useState(5);
 
//  // Ensure news is an array before filtering
//  const filteredAssets = Array.isArray(assets) ? assets?.filter(item =>
//      item.name?.toLowerCase().includes(searchQuery.toLowerCase())
//  ) : [];

//   // Ensure news is an array before filtering
//   const filteredSubscriptions = Array.isArray(subscriptions) ? subscriptions?.filter(item =>
//     item.user.id?.toString().toLowerCase().includes(searchSubscriptionsQuery.toLowerCase())||
//     item.user.email?.toLowerCase().includes(searchSubscriptionsQuery.toLowerCase())||
//     item.service_plan.id?.toString().toLowerCase().includes(searchSubscriptionsQuery.toLowerCase())||
//     item.service_plan.name?.toString().toLowerCase().includes(searchSubscriptionsQuery.toLowerCase())
// ) : [];
 
 
// //  // Sort news dynamically
// //  const sortedNews = [...filteredAssets].sort((a, b) => {
// //      if (sortBy === "asset_name") {
// //          return a.name.localeCompare(b.name);
// //      } else {
// //          return new Date(b.published_at) - new Date(a.published_at); // Latest first
// //      }
// //  });
 
//  // Handle Pagination
//  const handleChangePage = (_, newPage) => setPage(newPage);
//  const handleChangeRowsPerPage = (event) => {
//      setRowsPerPage(parseInt(event.target.value, 10));
//      setPage(0);
//  };

//   // Handle Pagination
//   const handleChangeSubscriptionPage = (_, newPage) => setSubscriptionPage(newPage);
//   const handleChangeRowsPerSubscriptionPage = (event) => {
//       setRowsPerSubscriptionPage(parseInt(event.target.value, 10));
//       setSubscriptionPage(0);
//   };

//  const mutation = useMutation({
//      mutationFn: createUrithiMonthlyService, // Ensure `createNews` is a function
//      onSuccess: (response) => {
//        queryClient.invalidateQueries({ queryKey: ["newsmonthlyservice"] }); // Uncomment if needed
//      //   setFormData({ title: "", content: "", image: null });
//        setOpen({ create: false, update: false, detail:false });
//        setToast({ open: true, message: "New monthly Service added successfully!", severity: "success" });
//        refetch()
//      },
//      onError: (error) => {
//        setToast({ open: true, message: "Failed to add new monthly Service. Try again.", severity: "error" });
//      }
//    });

//    // Update News Mutation
//        const updateMutation = useMutation({
//            mutationFn: updateUrithiMonthlyServices,
//            onSuccess: () => {
//                queryClient.invalidateQueries(["urithimonthlyUpdate"]);
//                // setFormDataEdit({ title: "", content: "", image: null });
//                setOpen({ create: false, update: false, detail:false });
//                setToast({ open: true, message: "Service updated successfully!", severity: "success" });
//            },
//            onError: (error) => {
//              setToast({ open: true, message: "Failed to update Service. Try again.", severity: "error" });
//            }
//        });
   
//        // Delete News Mutation
//        const deleteMutation = useMutation({
//            mutationFn: deleteUthiriMonthlyServices,
//            onSuccess: () => {
//                queryClient.invalidateQueries(["urithiMonthlyDelete"]);
//                setToast({ open: true, message: "Service deleted successfully!", severity: "success" });
//            },
//            onError: (error) => {
//                setToast({ open: true, message: "Failed to delete service. Try again.", severity: "error" });
//            }
//        });

//    const upformik = useFormik({
//         initialValues: {
//             name: '',
//             description: '',
//             price_per_month: '',
//             },
//             validationSchema: upValidationSchema,
//             onSubmit: (values) => { 
//             if (selectedItem.id) {
//             updateMutation.mutate({ id: selectedItem.id, data: values });
//             }
//             },
//         });

//    // Open drawer and set form data when updating
//   const handleOpenUpdateDrawer = (item) => {
//     setSelectedItem(item);
//     // Update formik values dynamically
//   upformik.setValues({
//     name:item.name || '',
//     description: item.description || '',
//     price_per_month: item.price_per_month || '',
//   });
//     setOpen({ create: false, update: true });
//   };

//    // Function to toggle create drawer
//   const toggleDrawer = (type, state) => () => {
//     setOpen({ create: type === "create" ? state : false, update: type === "update" ? state : false });
    
//   };

//    // Handle delete
//    const handleDelete = (id) => {
//     deleteMutation.mutate(id);
// };

//  const formik = useFormik({
//    initialValues: {
//         name: '',
//         description: '',
//         price_per_month: '',
//    },
//    validationSchema: validationSchema,
//    onSubmit: (values) => {
//        mutation.mutate(values);
//    },
//  });
 
 
//   if (isLoading) {
//           return (
//               <Container sx={{ textAlign: "center", marginTop: 5 }}>
//                   <Typography>Loading...</Typography>
//               </Container>
//           );
//       }
  
//       if (isError) {
//           return (
//               <Container sx={{ textAlign: "center", marginTop: 5 }}>
//                   <Typography variant="h6" color="error">
//                       Error fetching news. Please try again later.
//                   </Typography>
//               </Container>
//           );
//       }
//   return (
//     <Container>
//         {assets?.length === 0 ? ( 
//         <Container maxWidth="md">
//           <Box
//               sx={{
//                   mt: "4px",
//                   p: "4px",
//                   // borderRadius: 2,
//                   boxShadow: 0,
//                   backgroundColor: 'background.paper',
//               }}
//               >
//               <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                   Add New Monthly Service
//               </Typography>
//               <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                   Fill in the details for the monthly service plan
//               </Typography>
//               <form onSubmit={formik.handleSubmit}>
//                   <Grid container spacing={3}>
//                   {/* Title Field */}
//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       size="small"
//                       label="Service Name"
//                       variant="outlined"
//                       name="name"
//                       value={formik.values.name}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.name && Boolean(formik.errors.name)}
//                       helperText={formik.touched.name && formik.errors.name}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
                      
//                       }}
//                       />
//                   </Grid> 

//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       size="small"
//                       label="Service pay per month"
//                       name="price_per_month"
//                       type="number"
//                       InputProps={{
//                         startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                       }}
//                       value={formik.values.price_per_month}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.price_per_month && Boolean(formik.errors.price_per_month)}
//                       helperText={formik.touched.price_per_month && formik.errors.price_per_month}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
                      
//                       }}
//                       />
//                   </Grid>

//                    {/* Content Field */}
//                 <Grid item xs={12}>
//                     <TextField
//                     fullWidth
//                     label="Description"
//                     variant="outlined"
//                     size="small"
//                     multiline
//                     rows={6}
//                     name="description"
//                     value={formik.values.description}
//                     onChange={formik.handleChange}
//                     onBlur={formik.handleBlur}
//                     error={formik.touched.description && Boolean(formik.errors.description)}
//                     helperText={formik.touched.description && formik.errors.description}
//                     InputLabelProps={{
//                         sx: {
//                         color: "#555", // Default label color
//                         fontSize: 10,
//                         transition: "color 0.3s ease",
//                         "&:hover": {
//                             color: "#333"
//                         }, // Label color on hover
//                         "&.Mui-focused": { 
//                             color: "#001f3f",
//                             textTransform:"uppercase",
//                             fontWeight:500
//                         }, // Label color on focus
//                         },
//                     }}
//                     inputProps={{
//                         sx: {
//                         fontSize: 10, // Field content size
//                         color: "#333", // Field content color
//                         "&::placeholder": {
//                             color: "#aaa", // Placeholder color
//                             fontSize: 10,
//                         },
//                         },
//                     }}
//                     sx={{
//                         '& .MuiOutlinedInput-root': {
//                         borderRadius: "2px",
//                         "&.Mui-focused fieldset": { 
//                             borderColor: "#001f3f",
//                             borderRadius:"2px"
//                         }, // Focus border
//                         },
//                         '& .MuiFormHelperText-root': {
//                         fontSize: 9, // Smaller font size for validation errors
//                         color: 'error.main', // Red color for errors
//                         textTransform:"capitalize"
//                         },
//                     }}
//                     />
//                 </Grid>
                 
      
//                   {/* Submit Button */}
//                   <Grid item xs={12}>
//                       <Button
//                       type="submit"
//                       variant="contained"
//                       fullWidth
//                       size="large"
//                       sx={{
//                           borderRadius: "2px",
//                           textTransform: 'none',
//                           py: 1.5,
//                           fontSize: 10,
//                           boxShadow:0,
//                           backgroundColor:"#001f3f",                  
//                           "&:hover": {
//                               boxShadow: "none",
//                           },
//                       }}
//                       >
//                       Add New Monthly Service
//                       </Button>
//                   </Grid>
//                   </Grid>
//               </form>
//               </Box>
//       </Container>
//       ):(
//         open.create ?(
//             <Container maxWidth="md">
//                 <IconButton
//                     onClick={toggleDrawer("create", false)}
//                 >
//                     <ArrowBack/>
//                 </IconButton>
//             <Box
//                 sx={{
//                     mt: "4px",
//                     p: "4px",
//                     // borderRadius: 2,
//                     boxShadow: 0,
//                     backgroundColor: 'background.paper',
//                 }}
//                 >
//                 <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                     Add New Monthly Service
//                 </Typography>
//                 <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                     Fill in the details for the monthly service plan
//                 </Typography>
//                 <form onSubmit={formik.handleSubmit}>
//                     <Grid container spacing={3}>
//                     {/* Title Field */}
//                     <Grid item xs={12}>
//                         <TextField
//                         fullWidth
//                         size="small"
//                         label="Service Name"
//                         variant="outlined"
//                         name="name"
//                         value={formik.values.name}
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         error={formik.touched.name && Boolean(formik.errors.name)}
//                         helperText={formik.touched.name && formik.errors.name}
//                         InputLabelProps={{
//                             sx: {
//                             color: "#555", // Default label color
//                             fontSize: 10,
//                             transition: "color 0.3s ease",
//                             "&:hover": {
//                                 color: "#333"
//                             }, // Label color on hover
//                             "&.Mui-focused": { 
//                                 color: "#001f3f",
//                                 textTransform:"uppercase",
//                                 fontWeight:500
//                             }, // Label color on focus
//                             },
//                         }}
//                         inputProps={{
//                             sx: {
//                             fontSize: 10, // Field content size
//                             color: "#333", // Field content color
//                             "&::placeholder": {
//                                 color: "#aaa", // Placeholder color
//                                 fontSize: 10,
//                             },
//                             },
//                         }}
//                         sx={{
//                             '& .MuiOutlinedInput-root': {
//                             borderRadius: "2px",
//                             "&.Mui-focused fieldset": { 
//                                 borderColor: "#001f3f",
//                                 borderRadius:"2px"
//                             }, // Focus border
//                             },
//                             '& .MuiFormHelperText-root': {
//                             fontSize: 9, // Smaller font size for validation errors
//                             color: 'error.main', // Red color for errors
//                             textTransform:"capitalize"
//                             },
                        
//                         }}
//                         />
//                     </Grid> 
  
//                     <Grid item xs={12}>
//                         <TextField
//                         fullWidth
//                         size="small"
//                         label="Service pay per month"
//                         name="price_per_month"
//                         type="number"
//                         InputProps={{
//                           startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                         }}
//                         value={formik.values.price_per_month}
//                         onChange={formik.handleChange}
//                         onBlur={formik.handleBlur}
//                         error={formik.touched.price_per_month && Boolean(formik.errors.price_per_month)}
//                         helperText={formik.touched.price_per_month && formik.errors.price_per_month}
//                         InputLabelProps={{
//                             sx: {
//                             color: "#555", // Default label color
//                             fontSize: 10,
//                             transition: "color 0.3s ease",
//                             "&:hover": {
//                                 color: "#333"
//                             }, // Label color on hover
//                             "&.Mui-focused": { 
//                                 color: "#001f3f",
//                                 textTransform:"uppercase",
//                                 fontWeight:500
//                             }, // Label color on focus
//                             },
//                         }}
//                         inputProps={{
//                             sx: {
//                             fontSize: 10, // Field content size
//                             color: "#333", // Field content color
//                             "&::placeholder": {
//                                 color: "#aaa", // Placeholder color
//                                 fontSize: 10,
//                             },
//                             },
//                         }}
//                         sx={{
//                             '& .MuiOutlinedInput-root': {
//                             borderRadius: "2px",
//                             "&.Mui-focused fieldset": { 
//                                 borderColor: "#001f3f",
//                                 borderRadius:"2px"
//                             }, // Focus border
//                             },
//                             '& .MuiFormHelperText-root': {
//                             fontSize: 9, // Smaller font size for validation errors
//                             color: 'error.main', // Red color for errors
//                             textTransform:"capitalize"
//                             },
                        
//                         }}
//                         />
//                     </Grid>
  
//                      {/* Content Field */}
//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       label="Description"
//                       variant="outlined"
//                       size="small"
//                       multiline
//                       rows={6}
//                       name="description"
//                       value={formik.values.description}
//                       onChange={formik.handleChange}
//                       onBlur={formik.handleBlur}
//                       error={formik.touched.description && Boolean(formik.errors.description)}
//                       helperText={formik.touched.description && formik.errors.description}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
//                       }}
//                       />
//                   </Grid>
                   
        
//                     {/* Submit Button */}
//                     <Grid item xs={12}>
//                         <Button
//                         type="submit"
//                         variant="contained"
//                         fullWidth
//                         size="large"
//                         sx={{
//                             borderRadius: "2px",
//                             textTransform: 'none',
//                             py: 1.5,
//                             fontSize: 10,
//                             boxShadow:0,
//                             backgroundColor:"#001f3f",                  
//                             "&:hover": {
//                                 boxShadow: "none",
//                             },
//                         }}
//                         >
//                         Add New Monthly Service
//                         </Button>
//                     </Grid>
//                     </Grid>
//                 </form>
//                 </Box>
//         </Container>
//         ):open.update ?(
//             <Container maxWidth="md">
//                 <IconButton
//                     onClick={toggleDrawer("update", false)}
//                 >
//                     <ArrowBack/>
//                 </IconButton>
//             <Box
//                 sx={{
//                     mt: "4px",
//                     p: "4px",
//                     // borderRadius: 2,
//                     boxShadow: 0,
//                     backgroundColor: 'background.paper',
//                 }}
//                 >
//                 <Typography variant="h4" sx={{ fontWeight: 700, color: "#001f3f", fontSize:12, textTransform:"uppercase" }}>
//                     Update Monthly Service
//                 </Typography>
//                 <Typography variant="body1" sx={{mb:2, color: "text.secondary", fontSize: 10, textTransform:"capitalize" }}>
//                     Fill in the details for the monthly service plan
//                 </Typography>
//                 <form onSubmit={upformik.handleSubmit}>
//                     <Grid container spacing={3}>
//                     {/* Title Field */}
//                     <Grid item xs={12}>
//                         <TextField
//                         fullWidth
//                         size="small"
//                         label="Service Name"
//                         variant="outlined"
//                         name="name"
//                         value={upformik.values.name}
//                         onChange={upformik.handleChange}
//                         onBlur={upformik.handleBlur}
//                         error={upformik.touched.name && Boolean(upformik.errors.name)}
//                         helperText={upformik.touched.name && upformik.errors.name}
//                         InputLabelProps={{
//                             sx: {
//                             color: "#555", // Default label color
//                             fontSize: 10,
//                             transition: "color 0.3s ease",
//                             "&:hover": {
//                                 color: "#333"
//                             }, // Label color on hover
//                             "&.Mui-focused": { 
//                                 color: "#001f3f",
//                                 textTransform:"uppercase",
//                                 fontWeight:500
//                             }, // Label color on focus
//                             },
//                         }}
//                         inputProps={{
//                             sx: {
//                             fontSize: 10, // Field content size
//                             color: "#333", // Field content color
//                             "&::placeholder": {
//                                 color: "#aaa", // Placeholder color
//                                 fontSize: 10,
//                             },
//                             },
//                         }}
//                         sx={{
//                             '& .MuiOutlinedInput-root': {
//                             borderRadius: "2px",
//                             "&.Mui-focused fieldset": { 
//                                 borderColor: "#001f3f",
//                                 borderRadius:"2px"
//                             }, // Focus border
//                             },
//                             '& .MuiFormHelperText-root': {
//                             fontSize: 9, // Smaller font size for validation errors
//                             color: 'error.main', // Red color for errors
//                             textTransform:"capitalize"
//                             },
                        
//                         }}
//                         />
//                     </Grid> 
  
//                     <Grid item xs={12}>
//                         <TextField
//                         fullWidth
//                         size="small"
//                         label="Service pay per month"
//                         name="price_per_month"
//                         type="number"
//                         InputProps={{
//                           startAdornment: <InputAdornment position="start">TZS</InputAdornment>,
//                         }}
//                         value={upformik.values.price_per_month}
//                         onChange={upformik.handleChange}
//                         onBlur={upformik.handleBlur}
//                         error={upformik.touched.price_per_month && Boolean(upformik.errors.price_per_month)}
//                         helperText={upformik.touched.price_per_month && upformik.errors.price_per_month}
//                         InputLabelProps={{
//                             sx: {
//                             color: "#555", // Default label color
//                             fontSize: 10,
//                             transition: "color 0.3s ease",
//                             "&:hover": {
//                                 color: "#333"
//                             }, // Label color on hover
//                             "&.Mui-focused": { 
//                                 color: "#001f3f",
//                                 textTransform:"uppercase",
//                                 fontWeight:500
//                             }, // Label color on focus
//                             },
//                         }}
//                         inputProps={{
//                             sx: {
//                             fontSize: 10, // Field content size
//                             color: "#333", // Field content color
//                             "&::placeholder": {
//                                 color: "#aaa", // Placeholder color
//                                 fontSize: 10,
//                             },
//                             },
//                         }}
//                         sx={{
//                             '& .MuiOutlinedInput-root': {
//                             borderRadius: "2px",
//                             "&.Mui-focused fieldset": { 
//                                 borderColor: "#001f3f",
//                                 borderRadius:"2px"
//                             }, // Focus border
//                             },
//                             '& .MuiFormHelperText-root': {
//                             fontSize: 9, // Smaller font size for validation errors
//                             color: 'error.main', // Red color for errors
//                             textTransform:"capitalize"
//                             },
                        
//                         }}
//                         />
//                     </Grid>
  
//                      {/* Content Field */}
//                   <Grid item xs={12}>
//                       <TextField
//                       fullWidth
//                       label="Description"
//                       variant="outlined"
//                       size="small"
//                       multiline
//                       rows={6}
//                       name="description"
//                       value={upformik.values.description}
//                       onChange={upformik.handleChange}
//                       onBlur={upformik.handleBlur}
//                       error={upformik.touched.description && Boolean(upformik.errors.description)}
//                       helperText={upformik.touched.description && upformik.errors.description}
//                       InputLabelProps={{
//                           sx: {
//                           color: "#555", // Default label color
//                           fontSize: 10,
//                           transition: "color 0.3s ease",
//                           "&:hover": {
//                               color: "#333"
//                           }, // Label color on hover
//                           "&.Mui-focused": { 
//                               color: "#001f3f",
//                               textTransform:"uppercase",
//                               fontWeight:500
//                           }, // Label color on focus
//                           },
//                       }}
//                       inputProps={{
//                           sx: {
//                           fontSize: 10, // Field content size
//                           color: "#333", // Field content color
//                           "&::placeholder": {
//                               color: "#aaa", // Placeholder color
//                               fontSize: 10,
//                           },
//                           },
//                       }}
//                       sx={{
//                           '& .MuiOutlinedInput-root': {
//                           borderRadius: "2px",
//                           "&.Mui-focused fieldset": { 
//                               borderColor: "#001f3f",
//                               borderRadius:"2px"
//                           }, // Focus border
//                           },
//                           '& .MuiFormHelperText-root': {
//                           fontSize: 9, // Smaller font size for validation errors
//                           color: 'error.main', // Red color for errors
//                           textTransform:"capitalize"
//                           },
//                       }}
//                       />
//                   </Grid>
                   
        
//                     {/* Submit Button */}
//                     <Grid item xs={12}>
//                         <Button
//                         type="submit"
//                         variant="contained"
//                         fullWidth
//                         size="large"
//                         sx={{
//                             borderRadius: "2px",
//                             textTransform: 'none',
//                             py: 1.5,
//                             fontSize: 10,
//                             boxShadow:0,
//                             backgroundColor:"#001f3f",                  
//                             "&:hover": {
//                                 boxShadow: "none",
//                             },
//                         }}
//                         >
//                         Update Monthly Service
//                         </Button>
//                     </Grid>
//                     </Grid>
//                 </form>
//                 </Box>
//         </Container>
//         ):(
//             <Container maxWidth='xl'>
//                 <Tabs
      
//                     indicatorColor="#001f3f"
//                     textColor="#001f3f"
//                         sx={{
//                         backgroundColor:"#e8e8e8",
//                         marginBottom: '20px',
//                         '& .MuiTabs-indicator': {
//                             backgroundColor: '#001f3f', // Custom indicator color
//                         },
//                         }}
//                     value={tabValue}
//                     onChange={handleTabChange}
//                     >
//                         <Tab
//                         sx={{
//                             fontSize:10,
//                             fontWeight: 'bold',
//                             color: tabValue === 0 ? '#001f3f' : 'gray', // Active tab color
//                             '&:hover': {
//                             color: '#001f3f', // Hover color
//                             },
//                         }}  label="Recurring Subscriptions" />
//                         <Tab 
//                         label="Subscriptions"
//                         sx={{
//                             fontSize:10,
//                             fontWeight: 'bold',
//                             color: tabValue === 1 ? '#001f3f' : 'gray', // Active tab color
//                             '&:hover': {
//                             color: '#001f3f', // Hover color
//                             },
//                         }}
//                         />
//                     </Tabs>
//                     <Box hidden={tabValue !== 0}>
//                        <Typography variant="h2" component="h2" 
//                         sx={{ 
//                             fontSize:12,
//                             fontWeight:700,
//                             textTransform:"uppercase"
//                         }}
//                         >
//                             Service Plan Overview
//                         </Typography>
//                         <Typography variant="body1" component="h2" 
//                         sx={{ 
//                             marginBottom: 2,
//                             color: "text.secondary",
//                             textTransform:"capitalize",
//                             fontSize:10
//                         }}>
//                             Control and Modify Monthly Service Plans and Their Descriptions
//                         </Typography>
//                         <Button
//                         startIcon={<Add/>}
//                         variant="contained"
//                         // color="primary"
//                         elevation={0}
//                         disableElevation={true}
//                         onClick={toggleDrawer("create", true)}
//                         sx={{ 
//                             mb: 1,
//                             color:"#001f3f",
//                             borderRadius:"1px",
//                             fontSize:10,
//                             fontWeight:500,
//                             textTransform:"capitalize",
//                             background:"none",
//                             textDecoration:"underline"
//                         }}
//                         >
                        
//                         Click here to Add New Monthly service
//                         </Button>
//                         <Typography variant="h4" component="h4" 
//                         sx={{ 
//                             mt:2,mb:1,
//                             fontSize:10,
//                             textTransform:"uppercase",
//                             fontWeight:600
                            
//                             }}>
//                             Services List
//                         </Typography>
//                         {/* Responsive Search and Sort Controls */}
//                         <Grid container spacing={2} alignItems="center">
//                             <Grid item xs={12} sm={8}>
//                                 <TextField
//                                     label="Search by Service Name.."
//                                     variant="outlined"
//                                     size="small"
//                                     fullWidth
//                                     InputLabelProps={{
//                                     sx: {
//                                         color: "#555", // Default label color
//                                         fontSize: 10,
//                                         transition: "color 0.3s ease",
//                                         "&:hover": {
//                                             color: "#333"
//                                         }, // Label color on hover
//                                         "&.Mui-focused": { 
//                                             color: "#001f3f",
//                                             textTransform:"uppercase",
//                                             fontWeight:500
//                                         }, // Label color on focus
//                                     },
//                                     }}
//                                     inputProps={{
//                                     sx: {
//                                         fontSize: 10, // Field content size
//                                         color: "#333", // Field content color
//                                         "&::placeholder": {
//                                         color: "#aaa", // Placeholder color
//                                         fontSize: 10,
//                                         },
//                                     },
//                                     }}
//                                     sx={{
//                                     backgroundColor: "#e8e8e8",
//                                     borderRadius: "1px",
//                                     "& .MuiOutlinedInput-root": {
//                                         "& fieldset": { 
//                                             borderColor: "#ccc",  
//                                             borderRadius:"2px"
//                                         }, // Default border
//                                         "&:hover fieldset": { 
//                                             borderColor: "#0000",
//                                             borderRadius:"2px"
//                                         }, // Hover border
//                                         "&.Mui-focused fieldset": { 
//                                             borderColor: "#001f3f",
//                                             borderRadius:"2px"
//                                         }, // Focus border
//                                     },
//                                     }}
//                                     value={searchQuery}
//                                     onChange={(e) => setSearchQuery(e.target.value)}
//                                 />
//                             </Grid>
//                         </Grid>
//                         <TableContainer component={Paper} elevation={0}>
//                             <Table>
//                                 <TableHead>
//                                     <TableRow>
//                                         <TableCell sx={{
//                                             fontSize:8,
//                                             fontWeight:600,
//                                             textTransform:"uppercase"
//                                         }}>Name</TableCell>
//                                         <TableCell sx={{
//                                             fontSize:8,
//                                             fontWeight:600,
//                                             textTransform:"uppercase"
//                                         }}>Description</TableCell>
//                                         <TableCell sx={{
//                                             fontSize:8,
//                                             fontWeight:600,
//                                             textTransform:"uppercase"
//                                         }}>Price per month (TZS)</TableCell>
                                    
//                                         <TableCell sx={{
//                                             fontSize:8,
//                                             fontWeight:600,
//                                             textTransform:"uppercase"
//                                         }}>Actions</TableCell>
//                                     </TableRow>
//                                 </TableHead>
//                                 <TableBody>
//                                 {filteredAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => (
//                                         <TableRow key={asset.id}>
//                                         <TableCell>
//                                             <Typography 
//                                             sx={{
//                                                 fontSize:10,
//                                                 textTransform:"capitalize",
//                                             }} variant="body1" fontWeight="bold">
//                                                 {asset.name}
//                                             </Typography>
//                                         </TableCell>
//                                         <TableCell>
//                                             <Typography 
//                                             sx={{
//                                                 fontSize:10,
//                                                 textTransform:"capitalize",
//                                             }} variant="body1" fontWeight="bold">
//                                                 {asset.description}
//                                             </Typography>
//                                         </TableCell>
//                                         <TableCell>
//                                             <Typography 
//                                             sx={{
//                                                 fontSize:10,
//                                                 textTransform:"capitalize",
//                                             }} variant="body1" fontWeight="bold">
//                                                 {asset.price_per_month}
//                                             </Typography>
//                                         </TableCell>
//                                         <TableCell>
//                                             <Box  sx={{
//                                             display:"flex",
//                                             flexDirection:"row",
//                                             gap:"10px"
//                                             }}>
//                                                 <IconButton onClick={() => handleOpenUpdateDrawer(asset)} color="inherit">
//                                                     <Edit/> 
//                                                 </IconButton>
//                                                 <IconButton
//                                                 color='error'
//                                                 onClick={()=>handleDelete(asset.id)} 
//                                                 disabled={deleteMutation.isLoading}
//                                                 >
//                                                     <Delete/> 
//                                                 </IconButton>
//                                             </Box>
//                                         </TableCell>
//                                     </TableRow>
//                                     ))}
//                                     </TableBody>
//                             </Table>
//                         </TableContainer>
//                         <TablePagination
//                         rowsPerPageOptions={[5, 10, 25]}
//                         component="div"
//                         count={filteredAssets.length}
//                         rowsPerPage={rowsPerPage}
//                         page={page}
//                         onPageChange={handleChangePage}
//                         onRowsPerPageChange={handleChangeRowsPerPage}
//                         sx={{
//                             "& .MuiTablePagination-root": {
//                                 fontSize: 10, // Adjust overall pagination text size
//                                 fontWeight: 500,
//                                 // textTransform: "uppercase",
//                             },
//                             "& .MuiTablePagination-toolbar": {
//                                 fontSize: 10, // Adjust toolbar text size (e.g., "Rows per page")
//                                 // textTransform:"uppercase"
//                             },
//                             "& .MuiTablePagination-selectLabel": {
//                                 fontSize: 10, // Adjust "Rows per page" text size
//                             },
//                             "& .MuiTablePagination-input": {
//                                 fontSize: 10, // Adjust dropdown text size
//                             },
//                             "& .MuiTablePagination-actions button": {
//                                 fontSize: 10, // Adjust button (next/prev) text size
//                             },
//                             "& .MuiSelect-select": {
//                                 backgroundColor: "#f5f5f5", // Light gray dropdown background
//                                 borderRadius: "1px",
//                                 padding: "5px 10px",
//                                 fontSize: 10, // Adjust dropdown font size
//                             },
//                             "& .MuiTablePagination-displayedRows": {
//                                 fontSize: 10, // Adjust font size of "1–1 of 1"
//                                 fontWeight: 500,
//                                 // textTransform: "uppercase",
//                             },
//                         }}
//                         SelectProps={{
//                             MenuProps: {
//                                 PaperProps: {
//                                     sx: {
//                                         borderRadius: "1px", // Rounded border for dropdown
//                                         backgroundColor: "#fff", // Background color of dropdown
//                                         elevation:0,
//                                         boxShadow:"none",
//                                         border: "1px solid #ccc", // Optional: Adds a subtle border for separation
//                                         mt: 1.5, 
//                                         "& .MuiMenuItem-root": {
//                                             fontSize: 10, // Dropdown text size
//                                             fontWeight: 500, // Dropdown text weight
//                                             "&.Mui-selected": {
//                                                 backgroundColor: "#001f3f", // Active dropdown item color (red)
//                                                 color: "white",
//                                             },
//                                             "&:hover": {
//                                                 backgroundColor: "#CCDDEE", // Hover color (lighter red)
//                                             },
//                                         },
//                                     },
//                                 },
//                             },
//                         }}
//                     />
//                     </Box>
//                     <Box hidden={tabValue !== 1}>
//                         {subscriptions?.length === 0 ?(
//                             <Typography color='text.secondary' sx={{
//                                 textTransform:"capitalize",
//                                 fontSize:10,
//                                 textAlign:"center"
//                             }}>No Data yet</Typography>
//                         ):(
//                             <Container>
                                        
//                                 <Typography variant="h4" component="h4" 
//                                 sx={{ 
//                                     mt:2,mb:1,
//                                     fontSize:10,
//                                     textTransform:"uppercase",
//                                     fontWeight:600
                                    
//                                     }}>
//                                     Subscriptions List
//                                 </Typography>
                    
//                                 {/* Responsive Search and Sort Controls */}
//                                 <Grid container spacing={2} alignItems="center">
//                                     <Grid item xs={12} sm={8}>
//                                         <TextField
//                                             label="Search by service id, service name, client id, or client email.."
//                                             variant="outlined"
//                                             size="small"
//                                             fullWidth
//                                             InputLabelProps={{
//                                             sx: {
//                                                 color: "#555", // Default label color
//                                                 fontSize: 10,
//                                                 transition: "color 0.3s ease",
//                                                 "&:hover": {
//                                                     color: "#333"
//                                                 }, // Label color on hover
//                                                 "&.Mui-focused": { 
//                                                     color: "#001f3f",
//                                                     textTransform:"uppercase",
//                                                     fontWeight:500
//                                                 }, // Label color on focus
//                                             },
//                                             }}
//                                             inputProps={{
//                                             sx: {
//                                                 fontSize: 10, // Field content size
//                                                 color: "#333", // Field content color
//                                                 "&::placeholder": {
//                                                 color: "#aaa", // Placeholder color
//                                                 fontSize: 10,
//                                                 },
//                                             },
//                                             }}
//                                             sx={{
//                                             backgroundColor: "#e8e8e8",
//                                             borderRadius: "1px",
//                                             "& .MuiOutlinedInput-root": {
//                                                 "& fieldset": { 
//                                                     borderColor: "#ccc",  
//                                                     borderRadius:"2px"
//                                                 }, // Default border
//                                                 "&:hover fieldset": { 
//                                                     borderColor: "#0000",
//                                                     borderRadius:"2px"
//                                                 }, // Hover border
//                                                 "&.Mui-focused fieldset": { 
//                                                     borderColor: "#001f3f",
//                                                     borderRadius:"2px"
//                                                 }, // Focus border
//                                             },
//                                             }}
//                                             value={searchSubscriptionsQuery}
//                                             onChange={(e) => setSearchSubscriptionsQuery(e.target.value)}
//                                         />
//                                     </Grid>
//                                 </Grid>
//                                 <TableContainer component={Paper} elevation={0}>
//                                 <Table>
//                                     <TableHead>
//                                         <TableRow>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>Service Id</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>client id</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>
//                                                 client details
//                                             </TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>quantity(months)</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>amount</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>start date</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>end date</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>payment status</TableCell>
//                                             <TableCell sx={{
//                                                 fontSize:8,
//                                                 fontWeight:600,
//                                                 textTransform:"uppercase"
//                                             }}>payment method</TableCell>
                                            
//                                         </TableRow>
//                                     </TableHead>
//                                     <TableBody>
//                                     {filteredSubscriptions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => (
//                                             <TableRow key={asset.id}>
//                                             <TableCell>
//                                                 <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.service_plan.id}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                                 <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.user.id}
//                                                 </Typography>
//                                             </TableCell>  
                                            
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.user.name} - {asset.user.email}
//                                                 </Typography>
//                                                 </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.quantity}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.total_price}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.start_date}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.end_date}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.payment_status}
//                                                 </Typography>
//                                             </TableCell>
//                                             <TableCell>
//                                             <Typography 
//                                                 sx={{
//                                                     fontSize:10,
//                                                     textTransform:"capitalize",
//                                                 }} variant="body1" fontWeight="bold">
//                                                     {asset.payment_method}
//                                                 </Typography>
//                                             </TableCell>
                                            
                                            
//                                         </TableRow>
//                                         ))}
//                                         </TableBody>
//                                 </Table>
//                             </TableContainer>
//                             <TablePagination
//                             rowsPerPageOptions={[5, 10, 25]}
//                             component="div"
//                             count={filteredSubscriptions.length}
//                             rowsPerPage={rowsPerSubscriptionPage}
//                             page={subscriptionPage}
//                             onPageChange={handleChangeSubscriptionPage}
//                             onRowsPerPageChange={handleChangeRowsPerSubscriptionPage}
//                             sx={{
//                                 "& .MuiTablePagination-root": {
//                                     fontSize: 10, // Adjust overall pagination text size
//                                     fontWeight: 500,
//                                     // textTransform: "uppercase",
//                                 },
//                                 "& .MuiTablePagination-toolbar": {
//                                     fontSize: 10, // Adjust toolbar text size (e.g., "Rows per page")
//                                     // textTransform:"uppercase"
//                                 },
//                                 "& .MuiTablePagination-selectLabel": {
//                                     fontSize: 10, // Adjust "Rows per page" text size
//                                 },
//                                 "& .MuiTablePagination-input": {
//                                     fontSize: 10, // Adjust dropdown text size
//                                 },
//                                 "& .MuiTablePagination-actions button": {
//                                     fontSize: 10, // Adjust button (next/prev) text size
//                                 },
//                                 "& .MuiSelect-select": {
//                                     backgroundColor: "#f5f5f5", // Light gray dropdown background
//                                     borderRadius: "1px",
//                                     padding: "5px 10px",
//                                     fontSize: 10, // Adjust dropdown font size
//                                 },
//                                 "& .MuiTablePagination-displayedRows": {
//                                     fontSize: 10, // Adjust font size of "1–1 of 1"
//                                     fontWeight: 500,
//                                     // textTransform: "uppercase",
//                                 },
//                             }}
//                             SelectProps={{
//                                 MenuProps: {
//                                     PaperProps: {
//                                         sx: {
//                                             borderRadius: "1px", // Rounded border for dropdown
//                                             backgroundColor: "#fff", // Background color of dropdown
//                                             elevation:0,
//                                             boxShadow:"none",
//                                             border: "1px solid #ccc", // Optional: Adds a subtle border for separation
//                                             mt: 1.5, 
//                                             "& .MuiMenuItem-root": {
//                                                 fontSize: 10, // Dropdown text size
//                                                 fontWeight: 500, // Dropdown text weight
//                                                 "&.Mui-selected": {
//                                                     backgroundColor: "#001f3f", // Active dropdown item color (red)
//                                                     color: "white",
//                                                 },
//                                                 "&:hover": {
//                                                     backgroundColor: "#CCDDEE", // Hover color (lighter red)
//                                                 },
//                                             },
//                                         },
//                                     },
//                                 },
//                             }}
//                         />
                            
                            
//                     </Container>
//                                 )}
//                     </Box>
                
//             </Container>
//         )
//       )}

//       {/* Toast Notifications */}
//         <Snackbar
//         open={toast.open}
//         autoHideDuration={3000}
//         onClose={() => setToast({ open: false, message: "", severity: "" })}
//         >
//         <Alert severity={toast.severity} 
//         sx={{
//                 fontSize: 10, // Adjust font size
//                 fontWeight:500, // Make text bold
//                 width: "100%",
//                 borderRadius: "1px", // Rounded corners
//                 padding: "10px", // Adjust padding 
//                 textTransform:"capitalize"
//             }}
//         >
//             {toast.message}
//         </Alert>
//         </Snackbar>
//     </Container>
//   )
// }

// export default UrithiMonthlyServices
